import { useState, useMemo, useCallback, useEffect } from "react";
import { format, parseISO, addDays } from "date-fns";
import { usePaymentRuns, type NewRunInvoice } from "@/hooks/usePaymentRuns";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { formatCurrency, formatCurrencyWithCents } from "@/lib/currency";
import {
  Calculator,
  TrendingUp,
  Download,
  ArrowUpDown,
  DollarSign,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  CalendarIcon,
  Settings2,
  CheckCircle2,
  Save,
  Sparkles,
  X,
} from "lucide-react";
import { useVendorTermsLookup } from "@/hooks/useVendorTermsLookup";
import { aprToDiscount } from "@/lib/negotiation/terms";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import type { PARInvoiceRow } from "@/lib/par/csvInvoiceParser";
import { daysBetween } from "@/lib/par/dateUtils";

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

interface EarlyPayInvoice {
  idx: number; // stable identity (first row index in group)
  vendor_name: string;
  invoice_number: string | null;
  outstanding: number; // summed across all rows in group
  potential_savings: number; // summed across all rows in group
  savingsRatio: number;
  daysUntilEarly: number | null;
  discount_pct: number | null;
  due_date: string | null;
  early_pay_due_date: string | null;
  rowCount: number; // how many source rows were aggregated
}

interface VendorGroup {
  vendor: string;
  invoices: EarlyPayInvoice[];
  totalOutstanding: number;
  totalSavings: number;
  selectedCount: number;
  selectedAmount: number;
  selectedSavings: number;
}

interface OptimizeTabProps {
  invoices: PARInvoiceRow[];
  referenceDate?: string;
  loading?: boolean;
  paymentRunsEnabled?: boolean;
  runId?: string | null;
  onCancel?: () => void;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function formatAxisValue(v: number): string {
  if (v === 0) return "$0";
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
  return `$${v.toFixed(0)}`;
}

function buildEligibleInvoices(invoices: PARInvoiceRow[], today: string): EarlyPayInvoice[] {
  // Group by vendor_name + invoice_number so each invoice appears once
  const groups = new Map<string, { firstIdx: number; rows: PARInvoiceRow[] }>();
  for (let i = 0; i < invoices.length; i++) {
    const inv = invoices[i];
    const outstanding = inv.amount_invoiced - inv.amount_paid;
    if (outstanding <= 0) continue;
    if (inv.discount_pct == null || inv.discount_pct <= 0) continue;
    if (inv.potential_savings <= 0) continue;
    const key = `${inv.vendor_name}|${inv.invoice_number ?? ""}`;
    if (!groups.has(key)) groups.set(key, { firstIdx: i, rows: [] });
    groups.get(key)!.rows.push(inv);
  }

  const result: EarlyPayInvoice[] = [];
  for (const [, { firstIdx, rows }] of groups) {
    const totalOutstanding = rows.reduce((s, r) => s + (r.amount_invoiced - r.amount_paid), 0);
    const totalSavings = rows.reduce((s, r) => s + r.potential_savings, 0);
    const earlyDates = rows
      .map((r) => r.early_pay_due_date)
      .filter(Boolean)
      .sort() as string[];
    const minEarlyDate = earlyDates[0] ?? null;
    const daysUntilEarly = minEarlyDate ? daysBetween(minEarlyDate, today) : null;
    // Skip invoices with no early pay date or where the window has already closed
    if (!minEarlyDate) continue;
    if (daysUntilEarly !== null && daysUntilEarly < 0) continue;
    const first = rows[0];
    result.push({
      idx: firstIdx,
      vendor_name: first.vendor_name,
      invoice_number: first.invoice_number,
      outstanding: totalOutstanding,
      potential_savings: totalSavings,
      savingsRatio: totalOutstanding > 0 ? totalSavings / totalOutstanding : 0,
      daysUntilEarly,
      discount_pct: first.discount_pct,
      due_date: first.due_date,
      early_pay_due_date: minEarlyDate,
      rowCount: rows.length,
    });
  }
  return result.sort((a, b) => b.savingsRatio - a.savingsRatio);
}

/** Vendor-first greedy: fill all invoices from highest-yield vendor before moving to next */
function greedySelectVendorFirst(ranked: EarlyPayInvoice[], budget: number): Set<number> {
  // Group by vendor
  const vendorGroups = new Map<string, EarlyPayInvoice[]>();
  for (const inv of ranked) {
    if (!vendorGroups.has(inv.vendor_name)) vendorGroups.set(inv.vendor_name, []);
    vendorGroups.get(inv.vendor_name)!.push(inv);
  }
  // Sort vendors by total savings ratio (desc)
  const sortedVendors = Array.from(vendorGroups.entries()).sort(([, a], [, b]) => {
    const ratioA =
      a.reduce((s, i) => s + i.potential_savings, 0) / a.reduce((s, i) => s + i.outstanding, 0);
    const ratioB =
      b.reduce((s, i) => s + i.potential_savings, 0) / b.reduce((s, i) => s + i.outstanding, 0);
    return ratioB - ratioA;
  });
  const sel = new Set<number>();
  let remaining = budget;
  for (const [, invoices] of sortedVendors) {
    for (const inv of invoices) {
      if (inv.outstanding <= remaining) {
        sel.add(inv.idx);
        remaining -= inv.outstanding;
      }
    }
  }
  return sel;
}

/** Build the incremental cash → savings curve */
function buildIncrementalCurve(ranked: EarlyPayInvoice[]) {
  const points: { cash: number; savings: number }[] = [{ cash: 0, savings: 0 }];
  let cumCash = 0;
  let cumSavings = 0;
  for (const inv of ranked) {
    cumCash += inv.outstanding;
    cumSavings += inv.potential_savings;
    points.push({ cash: cumCash, savings: cumSavings });
  }
  return points;
}

/** Group eligible invoices by early_pay_due_date to show daily payment obligations */
function buildDailySpendData(invoices: EarlyPayInvoice[], today: string) {
  const map = new Map<string, { outstanding: number; savings: number; count: number }>();
  for (const inv of invoices) {
    const d = inv.early_pay_due_date;
    if (!d) continue;
    if (!map.has(d)) map.set(d, { outstanding: 0, savings: 0, count: 0 });
    const entry = map.get(d)!;
    entry.outstanding += inv.outstanding;
    entry.savings += inv.potential_savings;
    entry.count++;
  }
  const todayMs = new Date(today + "T00:00:00").getTime();
  return Array.from(map.entries())
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([date, { outstanding, savings, count }]) => {
      const daysAway = Math.round((new Date(date + "T00:00:00").getTime() - todayMs) / 86400000);
      return { date, outstanding, savings, count, daysAway };
    });
}

function exportSelectedAsCSV(ranked: EarlyPayInvoice[], selected: Set<number>) {
  const headers = ["Vendor", "Invoice #", "Amount", "Early Pay Due Date", "Potential Savings"];
  const rows = ranked
    .filter((inv) => selected.has(inv.idx))
    .map((inv) => [
      `"${inv.vendor_name}"`,
      inv.invoice_number ?? "",
      inv.outstanding.toFixed(2),
      inv.early_pay_due_date ?? "",
      inv.potential_savings.toFixed(2),
    ]);
  const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "early_pay_recommendations.csv";
  a.click();
  URL.revokeObjectURL(url);
}

type SortField = "vendor" | "amount" | "savings" | "ratio" | "earlyDue";
type SortDir = "asc" | "desc";

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export function OptimizeTab({
  invoices,
  referenceDate,
  loading,
  paymentRunsEnabled: _paymentRunsEnabled = false,
  runId,
  onCancel,
}: OptimizeTabProps) {
  const today = referenceDate || new Date().toISOString().split("T")[0];
  const { session } = useAuth();
  const { toast } = useToast();
  const { saveRun, getRun } = usePaymentRuns();
  const { getTermsForVendor } = useVendorTermsLookup();

  // Track which vendor nudges the user has dismissed this session
  const [dismissedNudges, setDismissedNudges] = useState<Set<string>>(new Set());

  // Per-vendor discount_pct overrides applied via "apply negotiated terms" nudge
  const [negotiatedOverrides, setNegotiatedOverrides] = useState<
    Map<string, { discount_pct: number; days_early: number }>
  >(new Map());

  // Payment run header state
  const defaultRunName = `Payment Run - ${format(new Date(), "MMM d, yyyy")}`;
  const [runName, setRunName] = useState(defaultRunName);
  const [runPayDate, setRunPayDate] = useState<Date>(() => addDays(new Date(), 7));
  const [runStatus, setRunStatus] = useState<"draft" | "processing" | "completed">("draft");
  const [runPayDateOpen, setRunPayDateOpen] = useState(false);
  const [savingRun, setSavingRun] = useState(false);
  const [loadedRunId, setLoadedRunId] = useState<string | null>(null);

  // isPast: pay_date is before today
  const isPast = runPayDate < new Date(new Date().toISOString().split("T")[0] + "T00:00:00");

  // Load existing run when runId changes
  useEffect(() => {
    if (!runId) {
      // Reset to defaults when no runId
      setRunName(defaultRunName);
      setRunPayDate(addDays(new Date(), 7));
      setRunStatus("draft");
      setLoadedRunId(null);
      return;
    }
    getRun(runId).then((run) => {
      if (run) {
        setRunName(run.name);
        setRunPayDate(parseISO(run.pay_date + "T00:00:00"));
        setRunStatus(run.status);
        setLoadedRunId(run.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  // Eligible invoices sorted by savings ratio
  const baseRanked = useMemo(() => buildEligibleInvoices(invoices, today), [invoices, today]);

  // Apply any negotiated-term overrides to recompute savings for that vendor
  const ranked = useMemo(() => {
    if (negotiatedOverrides.size === 0) return baseRanked;
    return baseRanked.map((inv) => {
      const override = negotiatedOverrides.get(inv.vendor_name);
      if (!override) return inv;
      const newDiscountPct = override.discount_pct;
      const newSavings = inv.outstanding * (newDiscountPct / 100);
      return {
        ...inv,
        discount_pct: newDiscountPct,
        potential_savings: newSavings,
        savingsRatio: inv.outstanding > 0 ? newSavings / inv.outstanding : 0,
      };
    });
  }, [baseRanked, negotiatedOverrides]);

  // Min invoice amount filter — must be declared before filteredRanked to avoid TDZ
  const [minAmountInput, setMinAmountInput] = useState("");
  const minAmount = parseFloat(minAmountInput.replace(/[^0-9.]/g, "")) || 0;

  const filteredRanked = useMemo(() => {
    const todayStr = format(new Date(), "yyyy-MM-dd");
    const runPayDateStr = format(runPayDate, "yyyy-MM-dd");
    return ranked.filter((inv) => {
      const d = inv.early_pay_due_date;
      // Must have an early pay date, be in the future, and be on or after the run's pay date
      if (!d || d < todayStr) return false;
      if (d < runPayDateStr) return false;
      if (minAmount > 0 && inv.outstanding < minAmount) return false;
      return true;
    });
  }, [ranked, runPayDate, minAmount]);

  const totalEligibleCost = useMemo(
    () => filteredRanked.reduce((s, r) => s + r.outstanding, 0),
    [filteredRanked]
  );
  const totalEligibleSavings = useMemo(
    () => filteredRanked.reduce((s, r) => s + r.potential_savings, 0),
    [filteredRanked]
  );

  // Incremental curve data
  const curveData = useMemo(() => buildIncrementalCurve(filteredRanked), [filteredRanked]);

  // Daily spend data — outstanding amount grouped by early_pay_due_date
  const dailySpendData = useMemo(
    () => buildDailySpendData(filteredRanked, today),
    [filteredRanked, today]
  );

  // Budget input
  const [budgetInput, setBudgetInput] = useState("");
  const budget = parseFloat(budgetInput.replace(/[^0-9.]/g, "")) || 0;
  const [calculated, setCalculated] = useState(false);
  const [dateStart, setDateStart] = useState<Date | null>(null);
  const [dateEnd, setDateEnd] = useState<Date | null>(null);

  // Selected invoices (auto-set by greedy, then user can toggle)
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [manualOverrides, setManualOverrides] = useState(false);

  // Sort
  const [sort, setSort] = useState<{ field: SortField; dir: SortDir }>({
    field: "earlyDue",
    dir: "asc",
  });

  const handleCalculate = useCallback(() => {
    if (budget <= 0) return;
    const sel = greedySelectVendorFirst(filteredRanked, budget);
    setSelected(sel);
    setManualOverrides(false);
    setCalculated(true);
  }, [budget, filteredRanked]);

  const handleReset = useCallback(() => {
    if (budget > 0) {
      const sel = greedySelectVendorFirst(filteredRanked, budget);
      setSelected(sel);
      setManualOverrides(false);
    }
  }, [budget, filteredRanked]);

  const handleSaveRun = useCallback(async () => {
    if (!runName.trim()) return;
    setSavingRun(true);
    const payDateStr = format(runPayDate, "yyyy-MM-dd");
    // Build selected invoices list
    const selectedInvoices: NewRunInvoice[] = filteredRanked
      .filter((inv) => selected.has(inv.idx))
      .map((inv) => ({
        vendor_name: inv.vendor_name,
        invoice_number: inv.invoice_number,
        outstanding: inv.outstanding,
        potential_savings: inv.potential_savings,
        early_pay_due_date: inv.early_pay_due_date,
      }));
    const result = await saveRun({
      id: loadedRunId,
      name: runName.trim(),
      pay_date: payDateStr,
      budget,
      invoices: selectedInvoices,
      createdBy: session?.user?.id ?? null,
    });
    setSavingRun(false);
    if (result) {
      setLoadedRunId(result.id);
      toast({
        title: "Payment run saved",
        description: `"${result.name}" saved successfully.`,
      });
      onCancel?.();
    }
  }, [
    runName,
    runPayDate,
    loadedRunId,
    selected,
    filteredRanked,
    budget,
    saveRun,
    session,
    toast,
    onCancel,
  ]);

  const toggleInvoice = useCallback(
    (idx: number, inv: EarlyPayInvoice) => {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(idx)) {
          next.delete(idx);
        } else {
          // Check if adding would exceed budget
          const currentCost = filteredRanked
            .filter((r) => next.has(r.idx))
            .reduce((s, r) => s + r.outstanding, 0);
          if (currentCost + inv.outstanding <= budget) {
            next.add(idx);
          }
        }
        return next;
      });
      setManualOverrides(true);
    },
    [filteredRanked, budget]
  );

  // Toggle all invoices for a vendor
  const toggleVendor = useCallback(
    (vendorInvoices: EarlyPayInvoice[]) => {
      setSelected((prev) => {
        const next = new Set(prev);
        const allSelected = vendorInvoices.every((inv) => next.has(inv.idx));
        if (allSelected) {
          // Deselect all for this vendor
          for (const inv of vendorInvoices) {
            next.delete(inv.idx);
          }
        } else {
          // Select as many as fit within budget
          const currentCost = filteredRanked
            .filter((r) => next.has(r.idx))
            .reduce((s, r) => s + r.outstanding, 0);
          let remaining = budget - currentCost;
          for (const inv of vendorInvoices) {
            if (!next.has(inv.idx) && inv.outstanding <= remaining) {
              next.add(inv.idx);
              remaining -= inv.outstanding;
            }
          }
        }
        return next;
      });
      setManualOverrides(true);
    },
    [filteredRanked, budget]
  );

  // Computed from selection
  const selectionStats = useMemo(() => {
    let cost = 0;
    let savings = 0;
    for (const inv of filteredRanked) {
      if (selected.has(inv.idx)) {
        cost += inv.outstanding;
        savings += inv.potential_savings;
      }
    }
    return { cost, savings, count: selected.size };
  }, [filteredRanked, selected]);

  // Table view filter: all vs accelerated only
  const [tableView, setTableView] = useState<"all" | "accelerated">("all");

  // Collapsed vendor rows — all collapsed by default
  const [collapsedVendors, setCollapsedVendors] = useState<Set<string>>(() => new Set<string>());
  // We initialise to "all collapsed" lazily once vendorGroups is known — see below
  const [allCollapsedInit, setAllCollapsedInit] = useState(false);

  // Sort the ranked list for display (always sort full filteredRanked; view filter applied in render)
  const sortedRanked = useMemo(() => {
    const list = [...filteredRanked];
    const dir = sort.dir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      switch (sort.field) {
        case "vendor":
          return dir * a.vendor_name.localeCompare(b.vendor_name);
        case "amount":
          return dir * (a.outstanding - b.outstanding);
        case "savings":
          return dir * (a.potential_savings - b.potential_savings);
        case "ratio":
          return dir * (a.savingsRatio - b.savingsRatio);
        case "earlyDue":
          return (
            dir * ((a.early_pay_due_date ?? "9999") < (b.early_pay_due_date ?? "9999") ? -1 : 1)
          );
        default:
          return 0;
      }
    });
    return list;
  }, [filteredRanked, sort]);

  // Base vendor groups (structure only, no selection state) — only recomputes when sorted list changes
  const baseVendorGroups = useMemo(() => {
    const map = new Map<string, EarlyPayInvoice[]>();
    for (const inv of sortedRanked) {
      if (!map.has(inv.vendor_name)) map.set(inv.vendor_name, []);
      map.get(inv.vendor_name)!.push(inv);
    }
    return Array.from(map.entries()).map(([vendor, invs]) => ({
      vendor,
      invoices: invs,
      totalOutstanding: invs.reduce((s, i) => s + i.outstanding, 0),
      totalSavings: invs.reduce((s, i) => s + i.potential_savings, 0),
    }));
  }, [sortedRanked]);

  // Vendor-grouped view with selection counts overlaid — cheap join, separate from structure
  const vendorGroups = useMemo((): VendorGroup[] => {
    return baseVendorGroups.map(({ vendor, invoices: invs, totalOutstanding, totalSavings }) => {
      let selectedCount = 0;
      let selectedAmount = 0;
      let selectedSavings = 0;
      for (const i of invs) {
        if (selected.has(i.idx)) {
          selectedCount++;
          selectedAmount += i.outstanding;
          selectedSavings += i.potential_savings;
        }
      }
      return {
        vendor,
        invoices: invs,
        totalOutstanding,
        totalSavings,
        selectedCount,
        selectedAmount,
        selectedSavings,
      };
    });
  }, [baseVendorGroups, selected]);

  // Once we have vendor groups, collapse all vendors by default (runs once)
  useEffect(() => {
    if (!allCollapsedInit && baseVendorGroups.length > 0) {
      setCollapsedVendors(new Set(baseVendorGroups.map((g) => g.vendor)));
      setAllCollapsedInit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseVendorGroups.length, allCollapsedInit]);

  const toggleVendorCollapse = useCallback((vendor: string) => {
    setCollapsedVendors((prev) => {
      const next = new Set(prev);
      if (next.has(vendor)) {
        next.delete(vendor);
      } else {
        next.add(vendor);
      }
      return next;
    });
  }, []);

  const areAllExpanded = collapsedVendors.size === 0;
  const toggleAllCollapsed = useCallback(() => {
    if (areAllExpanded) {
      setCollapsedVendors(new Set(baseVendorGroups.map((g) => g.vendor)));
    } else {
      setCollapsedVendors(new Set());
    }
  }, [areAllExpanded, baseVendorGroups]);

  const toggleSort = (field: SortField) => {
    setSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { field, dir: "desc" }
    );
  };

  const SortIcon = ({ field }: { field: SortField }) =>
    sort.field === field ? (
      sort.dir === "asc" ? (
        <ChevronUp className="w-3 h-3" />
      ) : (
        <ChevronDown className="w-3 h-3" />
      )
    ) : (
      <ArrowUpDown className="w-3 h-3 opacity-30" />
    );

  if (loading && ranked.length === 0) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-1">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-lg border p-4 space-y-2">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Payment Run header */}
        <div className="rounded-lg border border-border bg-card px-4 py-4 space-y-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h3 className="text-base font-semibold text-foreground leading-tight">Payment Run</h3>
            <div className="flex items-center gap-2 shrink-0">
              {isPast ? (
                <Badge className="text-[10px] border bg-muted/60 text-muted-foreground border-border">
                  Past – Read Only
                </Badge>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs gap-1.5"
                    onClick={onCancel}
                  >
                    <X className="w-3.5 h-3.5" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="h-8 text-xs gap-1.5"
                    onClick={handleSaveRun}
                    disabled={savingRun || !runName.trim()}
                  >
                    <Save className="w-3.5 h-3.5" />
                    {savingRun ? "Saving..." : "Save Run"}
                  </Button>
                </>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Run name */}
            <div className="sm:col-span-2 space-y-1">
              <Label className="text-[11px] text-muted-foreground uppercase tracking-wide font-semibold">
                Name
              </Label>
              <Input
                value={runName}
                onChange={(e) => setRunName(e.target.value)}
                placeholder={defaultRunName}
                disabled={isPast}
                className="h-8 text-sm"
              />
            </div>
            {/* Pay date */}
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground uppercase tracking-wide font-semibold">
                Pay Date
              </Label>
              <Popover open={runPayDateOpen} onOpenChange={setRunPayDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full h-8 text-sm justify-start gap-2 font-normal",
                      isPast && "opacity-60 cursor-not-allowed"
                    )}
                    disabled={isPast}
                  >
                    <CalendarIcon className="w-3.5 h-3.5 shrink-0" />
                    {format(runPayDate, "MMM d, yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={runPayDate}
                    onSelect={(d) => {
                      if (d) {
                        setRunPayDate(d);
                        setRunPayDateOpen(false);
                      }
                    }}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground">Status:</span>
            <Badge
              className={cn(
                "text-[10px] border",
                runStatus === "draft" && "bg-muted/60 text-muted-foreground border-border",
                runStatus === "processing" && "bg-warning/10 text-warning border-warning/20",
                runStatus === "completed" && "bg-primary/10 text-primary border-primary/20"
              )}
            >
              {runStatus.charAt(0).toUpperCase() + runStatus.slice(1)}
            </Badge>
          </div>
        </div>

        {/* Past run notice */}
        {isPast && (
          <div className="rounded-lg border border-muted bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
            This run has passed and can no longer be edited.
          </div>
        )}

        {/* Unified 2-col layout: Available Cash + stats (left) + Incremental Cash Impact (right) */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Left column: Available Cash stacked on top of summary stats */}
          <div className="flex flex-col gap-4">
            {/* Budget input card */}
            <Card className="p-4 border-l-4 border-l-primary border-primary/25 bg-primary/5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center ring-1 ring-primary/20">
                  <DollarSign className="w-[18px] h-[18px] text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-foreground tracking-tight">
                    Available Cash
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    How much can you deploy for early payment?
                  </p>
                </div>
                {/* Settings gear icon — min invoice amount filter */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" className="relative h-7 w-7 p-0 shrink-0">
                      <Settings2 className="w-4 h-4 text-muted-foreground" />
                      {minAmount > 0 && (
                        <span className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-primary" />
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-4" align="end">
                    <div className="space-y-3">
                      <Label className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">
                        Min Invoice Amount
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                          $
                        </span>
                        <Input
                          value={minAmountInput}
                          onChange={(e) => {
                            setMinAmountInput(e.target.value);
                            setCalculated(false);
                          }}
                          placeholder="0 (no minimum)"
                          className="pl-6 h-9 text-base"
                        />
                      </div>
                      {minAmount > 0 && (
                        <button
                          className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                          onClick={() => {
                            setMinAmountInput("");
                            setCalculated(false);
                            setSelected(new Set());
                            setManualOverrides(false);
                          }}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-3">
                {/* Amount to Deploy — primary, full width, prominent */}
                <div>
                  <Label className="text-[11px] text-foreground/70 mb-2 block font-bold uppercase tracking-widest">
                    Amount to Deploy
                  </Label>
                  <div className="relative flex items-center">
                    <div className="absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center bg-muted/60 border border-r-0 border-border/60 rounded-l-md">
                      <span className="text-lg text-muted-foreground font-medium select-none">
                        $
                      </span>
                    </div>
                    <Input
                      value={budgetInput}
                      onChange={(e) => {
                        setBudgetInput(e.target.value);
                        setCalculated(false);
                      }}
                      onKeyDown={(e) => e.key === "Enter" && budget > 0 && handleCalculate()}
                      placeholder={totalEligibleCost.toLocaleString("en-US", {
                        maximumFractionDigits: 0,
                      })}
                      className="pl-14 h-14 text-2xl font-semibold tracking-tight rounded-l-none border-l-0 focus-visible:ring-primary/50 bg-background/80"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleCalculate}
                  disabled={budget <= 0 || isPast}
                  className="w-full h-10 gap-1.5"
                >
                  <Calculator className="w-4 h-4" />
                  Optimize
                </Button>

                <p className="text-[10px] text-muted-foreground text-center">
                  {filteredRanked.length} eligible invoices · {formatCurrency(totalEligibleCost)}{" "}
                  total · {formatCurrencyWithCents(totalEligibleSavings)} in potential savings
                </p>
              </div>
            </Card>
          </div>

          {/* Right column: Incremental Cash Impact chart */}
          <Card className="p-4">
            <h4 className="text-sm font-semibold mb-1 flex items-center gap-1.5">
              <TrendingUp className="w-4 h-4 text-primary" />
              Incremental Cash Impact
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              Hover the curve to see savings and discount rate at each budget level.
            </p>

            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={curveData} margin={{ top: 5, right: 10, left: 0, bottom: 24 }}>
                  <defs>
                    <linearGradient id="savingsGradTop" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="cash"
                    tickFormatter={formatAxisValue}
                    tick={{ fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    label={{
                      value: "Cash Deployed",
                      position: "insideBottom",
                      offset: -8,
                      fontSize: 12,
                      fill: "hsl(var(--muted-foreground))",
                    }}
                  />
                  <YAxis
                    tickFormatter={formatAxisValue}
                    tick={{ fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    width={60}
                    label={{
                      value: "Savings",
                      angle: -90,
                      position: "insideLeft",
                      offset: 10,
                      fontSize: 12,
                      fill: "hsl(var(--muted-foreground))",
                    }}
                  />
                  <RechartsTooltip
                    content={({ payload }) => {
                      if (!payload?.length) return null;
                      const cash: number = payload[0].payload.cash ?? 0;
                      const savings: number = payload[0].payload.savings ?? 0;
                      const discountRate = cash > 0 ? (savings / cash) * 100 : 0;
                      return (
                        <div
                          style={{
                            fontSize: 13,
                            borderRadius: 8,
                            border: "1px solid hsl(var(--border))",
                            background: "hsl(var(--background))",
                            padding: "6px 10px",
                          }}
                        >
                          <p className="text-muted-foreground">
                            Cash deployed: {formatCurrency(cash)}
                          </p>
                          <p className="font-semibold">
                            Savings: {formatCurrencyWithCents(savings)}
                          </p>
                          <p className="text-muted-foreground">
                            Avg discount per invoice paid: {discountRate.toFixed(1)}%
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Area
                    type="stepAfter"
                    dataKey="savings"
                    stroke="hsl(var(--success))"
                    fill="url(#savingsGradTop)"
                    strokeWidth={2}
                  />
                  {budget > 0 && (
                    <ReferenceLine
                      x={budget}
                      stroke="hsl(var(--primary))"
                      strokeDasharray="4 4"
                      strokeWidth={1.5}
                      label={{
                        value: "Budget",
                        position: "top",
                        fontSize: 12,
                        fill: "hsl(var(--primary))",
                      }}
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Daily spend chart — savings at risk by early-pay deadline */}
        {dailySpendData.length > 0 && (
          <Card className="p-4">
            <div className="flex items-start justify-between mb-1">
              <div>
                <h4 className="text-sm font-semibold flex items-center gap-1.5">
                  <CalendarIcon className="w-4 h-4 text-primary" />
                  Savings at Risk by Deadline
                </h4>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Each bar is the total payment due by that date. Missing a deadline forfeits those
                  savings.
                </p>
              </div>
              <div className="flex items-center gap-3 text-[10px] text-muted-foreground shrink-0">
                <span className="flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-sm bg-destructive/70" />
                  ≤2 days
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-sm bg-warning/70" />
                  3–5 days
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-sm bg-primary/60" />
                  6+ days
                </span>
              </div>
            </div>
            <div className="h-48 mt-3">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={dailySpendData}
                  margin={{ top: 4, right: 10, left: 0, bottom: 0 }}
                  barCategoryGap="30%"
                >
                  <XAxis
                    dataKey="date"
                    tickFormatter={(v: string) => {
                      const d = new Date(v + "T00:00:00");
                      return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                    }}
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={formatAxisValue}
                    tick={{ fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={58}
                  />
                  <RechartsTooltip
                    cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }}
                    content={({ payload }) => {
                      if (!payload?.length) return null;
                      const d = payload[0].payload as {
                        date: string;
                        outstanding: number;
                        savings: number;
                        count: number;
                        daysAway: number;
                      };
                      const dateLabel = new Date(d.date + "T00:00:00").toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      });
                      const urgency = d.daysAway <= 2 ? "Today/tomorrow" : `In ${d.daysAway} days`;
                      return (
                        <div
                          style={{
                            fontSize: 12,
                            borderRadius: 8,
                            border: "1px solid hsl(var(--border))",
                            background: "hsl(var(--background))",
                            padding: "8px 12px",
                            minWidth: 180,
                          }}
                        >
                          <p className="font-semibold mb-1">
                            {dateLabel}{" "}
                            <span className="text-muted-foreground font-normal text-[11px]">
                              ({urgency})
                            </span>
                          </p>
                          <p className="text-muted-foreground">
                            Payment needed:{" "}
                            <span className="font-semibold text-foreground">
                              {formatCurrency(d.outstanding)}
                            </span>
                          </p>
                          <p className="text-success font-semibold">
                            Savings captured: {formatCurrencyWithCents(d.savings)}
                          </p>
                          <p className="text-muted-foreground mt-0.5">
                            {d.count} invoice{d.count !== 1 ? "s" : ""}
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="outstanding" radius={[3, 3, 0, 0]}>
                    {dailySpendData.map((entry, i) => (
                      <Cell
                        key={i}
                        fill={
                          entry.daysAway <= 2
                            ? "hsl(var(--destructive) / 0.7)"
                            : entry.daysAway <= 5
                              ? "hsl(var(--warning) / 0.7)"
                              : "hsl(var(--primary) / 0.6)"
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}

        {/* Full-width metrics row */}
        {calculated && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card className="p-4 border-success/30 bg-success/5">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">
                Total Savings
              </span>
              <div className="flex items-baseline gap-2 mt-0.5">
                <p className="text-xl font-bold text-success leading-tight">
                  {formatCurrencyWithCents(selectionStats.savings)}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">from {selectionStats.count} invoices</p>
            </Card>
            <Card className="p-4 border-primary/20 bg-primary/[0.02]">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">
                ROI
              </span>
              <p className="text-xl font-bold text-primary leading-tight mt-0.5">
                {selectionStats.cost > 0
                  ? ((selectionStats.savings / selectionStats.cost) * 100).toFixed(1) + "%"
                  : "—"}
              </p>
              <p className="text-xs text-muted-foreground">savings ÷ cash deployed</p>
            </Card>
            <Card className="p-4">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">
                Cash Deployed
              </span>
              <p className="text-xl font-bold text-foreground leading-tight mt-0.5">
                {formatCurrency(selectionStats.cost)}
              </p>
              <p className="text-xs text-muted-foreground">of {formatCurrency(budget)} budget</p>
            </Card>
            <Card className="p-4">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">
                Remaining Budget
              </span>
              <p className="text-xl font-bold text-foreground leading-tight mt-0.5">
                {formatCurrency(Math.max(0, budget - selectionStats.cost))}
              </p>
              <p className="text-xs text-muted-foreground">unallocated cash</p>
            </Card>
          </div>
        )}

        {/* Budget utilization bar */}
        {calculated && budget > 0 && (
          <div className="space-y-1">
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>Budget utilization</span>
              <span>
                {formatCurrency(selectionStats.cost)} of {formatCurrency(budget)} (
                {((selectionStats.cost / budget) * 100).toFixed(1)}%)
              </span>
            </div>
            <div className="h-2.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-success rounded-full transition-all duration-500"
                style={{
                  width: `${Math.min((selectionStats.cost / budget) * 100, 100).toFixed(1)}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Negotiated-terms nudge banners — one per vendor that has agreed terms
            differing from what their invoice rows currently reflect */}
        {(() => {
          const nudgeable = baseVendorGroups.flatMap((g) => {
            if (dismissedNudges.has(g.vendor)) return [];
            const terms = getTermsForVendor(g.vendor);
            if (!terms?.agreed_apr || !terms?.agreed_days_early) return [];
            const agreedDiscount = aprToDiscount(terms.agreed_apr, terms.agreed_days_early);
            // Already applied via override — don't re-nudge
            if (negotiatedOverrides.has(g.vendor)) return [];
            // Current avg discount for this vendor's invoices
            const validDiscounts = g.invoices
              .map((i) => i.discount_pct)
              .filter((d): d is number => d != null);
            const avgCurrentDiscount =
              validDiscounts.length > 0
                ? validDiscounts.reduce((s, d) => s + d, 0) / validDiscounts.length
                : null;
            // Differs if agreed discount differs by more than 0.01% from current
            if (avgCurrentDiscount !== null && Math.abs(agreedDiscount - avgCurrentDiscount) < 0.01)
              return [];
            return [
              {
                vendor: g.vendor,
                agreedDiscount,
                agreedApr: terms.agreed_apr,
                daysEarly: terms.agreed_days_early,
              },
            ];
          });
          if (nudgeable.length === 0) return null;
          return (
            <div className="space-y-2">
              {nudgeable.map(({ vendor, agreedDiscount, agreedApr, daysEarly }) => (
                <div
                  key={vendor}
                  role="status"
                  aria-label={`Negotiated terms available for ${vendor}`}
                  className="flex items-center gap-3 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs"
                >
                  <Sparkles className="w-3.5 h-3.5 text-primary shrink-0" />
                  <span className="flex-1 text-foreground">
                    Negotiated terms available for <span className="font-semibold">{vendor}</span>
                    {" — "}
                    {agreedDiscount.toFixed(2)}% discount ({agreedApr.toFixed(1)}% APR, {daysEarly}d
                    early)
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-[11px] gap-1 shrink-0"
                    onClick={() => {
                      setNegotiatedOverrides((prev) => {
                        const next = new Map(prev);
                        next.set(vendor, { discount_pct: agreedDiscount, days_early: daysEarly });
                        return next;
                      });
                    }}
                    data-testid={`apply-terms-${vendor}`}
                  >
                    Apply
                  </Button>
                  <button
                    className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                    aria-label={`Dismiss negotiated terms nudge for ${vendor}`}
                    onClick={() => setDismissedNudges((prev) => new Set([...prev, vendor]))}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Invoice selection table — grouped by vendor */}
        <div className="flex gap-4 items-start">
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {/* View toggle */}
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center rounded-md border border-border overflow-hidden text-[11px] font-medium">
                    <button
                      className={cn(
                        "px-3 py-1 transition-colors",
                        tableView === "all"
                          ? "bg-foreground text-background"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                      onClick={() => setTableView("all")}
                    >
                      <span className="flex items-center gap-1">
                        All Early Pay Invoices ({filteredRanked.length})
                        {minAmount > 0 && (
                          <span className="text-[10px] bg-primary/15 text-primary px-1.5 py-0.5 rounded font-medium">
                            &gt;{formatCurrency(minAmount)}
                          </span>
                        )}
                      </span>
                    </button>
                    <button
                      className={cn(
                        "px-3 py-1 transition-colors border-l border-border",
                        tableView === "accelerated"
                          ? "bg-success text-white"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                      onClick={() => setTableView("accelerated")}
                    >
                      Accelerated ({selected.size})
                    </button>
                  </div>
                  <span className="text-[10px] text-muted-foreground">
                    {"All upcoming early pay deadlines"}
                    {minAmount > 0 && ` · min ${formatCurrency(minAmount)}`}
                  </span>
                </div>
                {manualOverrides && (
                  <Badge variant="outline" className="text-[9px] border-warning/40 text-warning">
                    Modified
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {manualOverrides && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-[10px] gap-1"
                    onClick={handleReset}
                  >
                    <RefreshCw className="w-3 h-3" />
                    Reset to Optimal
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-[10px] gap-1"
                  onClick={() => exportSelectedAsCSV(filteredRanked, selected)}
                  disabled={selected.size === 0}
                >
                  <Download className="w-3 h-3" />
                  Export CSV
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-[10px] gap-1"
                  onClick={toggleAllCollapsed}
                >
                  {areAllExpanded ? (
                    <>
                      <ChevronRight className="w-3 h-3" />
                      Collapse all
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-3 h-3" />
                      Expand all
                    </>
                  )}
                </Button>
              </div>
            </div>

            {sortedRanked.length === 0 ? (
              (() => {
                const isDateFiltered = !!(dateStart || dateEnd);
                return (
                  <div className="border rounded-md overflow-hidden">
                    <div className="flex flex-col items-center justify-center py-16 px-8 text-center gap-4">
                      {isDateFiltered ? (
                        <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center">
                          <CalendarIcon className="w-6 h-6 text-muted-foreground/60" />
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                          <CheckCircle2 className="w-6 h-6 text-success/60" />
                        </div>
                      )}
                      <div className="space-y-1.5 max-w-sm">
                        <p className="text-sm font-semibold text-foreground">
                          {isDateFiltered
                            ? "No invoices in this date range"
                            : "No early payment opportunities right now"}
                        </p>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {isDateFiltered
                            ? "No invoices have early payment deadlines on or after the pay date and within the selected window. Try expanding the date range or adjusting the pay date."
                            : "All invoices are past their early payment deadline relative to the selected pay date, or no invoices with discount terms have been uploaded yet. Try moving the pay date earlier."}
                        </p>
                      </div>
                      {isDateFiltered && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs gap-1.5"
                          onClick={() => {
                            setDateStart(null);
                            setDateEnd(null);
                            setCalculated(false);
                            setSelected(new Set());
                            setManualOverrides(false);
                          }}
                        >
                          <CalendarIcon className="w-3.5 h-3.5" />
                          Clear date filter
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="border rounded-md overflow-hidden">
                <div className="overflow-auto max-h-[55vh]">
                  <Table className="table-fixed">
                    <colgroup>
                      {calculated && <col style={{ width: "36px" }} />}
                      <col style={{ width: calculated ? "26%" : "30%" }} />
                      <col style={{ width: "15%" }} />
                      <col style={{ width: "14%" }} />
                      <col style={{ width: "15%" }} />
                      <col style={{ width: "12%" }} />
                      <col style={{ width: "14%" }} />
                    </colgroup>
                    <TableHeader className="sticky top-0 z-10">
                      <TableRow className="bg-background hover:bg-background [&_th]:py-1.5 [&_th]:px-2 [&_th]:text-[11px] [&_th]:font-semibold">
                        {calculated && (
                          <TableHead className="w-9">
                            <Checkbox
                              checked={
                                selected.size === filteredRanked.length && filteredRanked.length > 0
                              }
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  const sel = greedySelectVendorFirst(filteredRanked, budget);
                                  setSelected(sel);
                                } else {
                                  setSelected(new Set());
                                }
                              }}
                              className="h-3.5 w-3.5"
                            />
                          </TableHead>
                        )}
                        <TableHead>
                          <button
                            className="flex items-center gap-1 hover:text-foreground"
                            onClick={() => toggleSort("vendor")}
                          >
                            Vendor <SortIcon field="vendor" />
                          </button>
                        </TableHead>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>
                          <button
                            className="flex items-center gap-1 hover:text-foreground justify-center w-full"
                            onClick={() => toggleSort("amount")}
                          >
                            Amount <SortIcon field="amount" />
                          </button>
                        </TableHead>
                        <TableHead>
                          <button
                            className="flex items-center gap-1 hover:text-foreground"
                            onClick={() => toggleSort("earlyDue")}
                          >
                            Early Pay Due <SortIcon field="earlyDue" />
                          </button>
                        </TableHead>
                        <TableHead>
                          <button
                            className="flex items-center gap-1 hover:text-foreground justify-center w-full"
                            onClick={() => toggleSort("ratio")}
                          >
                            Disc % <SortIcon field="ratio" />
                          </button>
                        </TableHead>
                        <TableHead>
                          <button
                            className="flex items-center gap-1 hover:text-foreground justify-end w-full"
                            onClick={() => toggleSort("savings")}
                          >
                            Savings <SortIcon field="savings" />
                          </button>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedRanked.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={calculated ? 7 : 6}
                            className="py-12 text-center text-sm text-muted-foreground"
                          >
                            <Calculator className="w-8 h-8 mx-auto mb-2 opacity-20" />
                            No invoices with open early payment windows
                          </TableCell>
                        </TableRow>
                      )}
                      {vendorGroups.map((group) => {
                        // In "accelerated" view, skip vendor groups with no selected invoices
                        if (tableView === "accelerated" && group.selectedCount === 0) return null;

                        const anyVendorSelected =
                          group.invoices.length > 0 &&
                          group.invoices.some((i) => selected.has(i.idx));
                        const allVendorSelected =
                          group.invoices.length > 0 &&
                          group.invoices.every((i) => selected.has(i.idx));
                        const isCollapsed = collapsedVendors.has(group.vendor);

                        return (
                          <TableRow
                            key={`vendor-${group.vendor}`}
                            className="p-0 hover:bg-transparent"
                          >
                            <TableCell colSpan={calculated ? 7 : 6} className="p-0">
                              <table className="w-full border-collapse table-fixed">
                                <colgroup>
                                  {calculated && <col style={{ width: "36px" }} />}
                                  <col style={{ width: calculated ? "26%" : "30%" }} />
                                  <col style={{ width: "15%" }} />
                                  <col style={{ width: "14%" }} />
                                  <col style={{ width: "15%" }} />
                                  <col style={{ width: "12%" }} />
                                  <col style={{ width: "14%" }} />
                                </colgroup>
                                <tbody>
                                  {/* Vendor header row */}
                                  <tr
                                    className="bg-muted/30 border-b border-t border-border/60 cursor-pointer hover:bg-muted/50 transition-colors"
                                    onClick={() => toggleVendorCollapse(group.vendor)}
                                  >
                                    {calculated && (
                                      <td className="py-1.5 px-2" style={{ width: "36px" }}>
                                        <Checkbox
                                          checked={anyVendorSelected}
                                          data-state={
                                            anyVendorSelected && !allVendorSelected
                                              ? "indeterminate"
                                              : undefined
                                          }
                                          onCheckedChange={() => toggleVendor(group.invoices)}
                                          className="h-3.5 w-3.5"
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                      </td>
                                    )}
                                    <td
                                      className="py-1.5 px-2"
                                      style={{ width: calculated ? "26%" : "30%" }}
                                      colSpan={2}
                                    >
                                      <span className="flex items-center gap-1">
                                        {isCollapsed ? (
                                          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                        ) : (
                                          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                        )}
                                        <span
                                          className="text-[11px] font-semibold text-foreground truncate"
                                          title={group.vendor}
                                        >
                                          {group.vendor}
                                        </span>
                                        <span className="text-[10px] text-muted-foreground shrink-0">
                                          ({group.invoices.length})
                                        </span>
                                      </span>
                                    </td>
                                    <td
                                      className="py-1.5 px-2 text-center"
                                      style={{ width: "14%" }}
                                    >
                                      <span className="text-[11px] font-semibold text-foreground">
                                        {calculated && group.selectedAmount > 0
                                          ? formatCurrency(group.selectedAmount)
                                          : formatCurrency(group.totalOutstanding)}
                                      </span>
                                      {calculated &&
                                        group.selectedAmount > 0 &&
                                        group.selectedAmount !== group.totalOutstanding && (
                                          <span className="text-[9px] text-muted-foreground block">
                                            of {formatCurrency(group.totalOutstanding)}
                                          </span>
                                        )}
                                    </td>
                                    <td className="py-1.5 px-2" style={{ width: "15%" }}>
                                      {(() => {
                                        const dates = group.invoices
                                          .map((i) => i.early_pay_due_date)
                                          .filter(Boolean)
                                          .sort() as string[];
                                        const earliest = dates[0];
                                        return earliest ? (
                                          <span className="text-[10px] text-muted-foreground">
                                            {new Date(earliest).toLocaleDateString()}
                                          </span>
                                        ) : null;
                                      })()}
                                    </td>
                                    <td
                                      className="py-1.5 px-2 text-center"
                                      style={{ width: "12%" }}
                                    >
                                      {(() => {
                                        const validDiscounts = group.invoices
                                          .map((i) => i.discount_pct)
                                          .filter((d): d is number => d != null);
                                        if (validDiscounts.length === 0) return null;
                                        const avg =
                                          validDiscounts.reduce((s, d) => s + d, 0) /
                                          validDiscounts.length;
                                        return (
                                          <span className="text-[10px] text-muted-foreground">
                                            {avg.toFixed(1)}%
                                          </span>
                                        );
                                      })()}
                                    </td>
                                    <td className="py-1.5 px-2 text-right" style={{ width: "14%" }}>
                                      <span className="text-[11px] font-bold text-success">
                                        {calculated && group.selectedSavings > 0
                                          ? formatCurrencyWithCents(group.selectedSavings)
                                          : formatCurrencyWithCents(group.totalSavings)}
                                      </span>
                                      {calculated &&
                                        group.selectedSavings > 0 &&
                                        group.selectedSavings !== group.totalSavings && (
                                          <span className="text-[9px] text-muted-foreground block text-right">
                                            of {formatCurrencyWithCents(group.totalSavings)}
                                          </span>
                                        )}
                                    </td>
                                  </tr>
                                  {/* Invoice rows for this vendor — hidden when collapsed */}
                                  {!isCollapsed &&
                                    group.invoices.map((inv) => {
                                      const isSelected = selected.has(inv.idx);
                                      if (tableView === "accelerated" && !isSelected) return null;
                                      const currentCost = selectionStats.cost;
                                      const canAdd =
                                        !isSelected &&
                                        (budget <= 0 || currentCost + inv.outstanding <= budget);
                                      return (
                                        <tr
                                          key={inv.idx}
                                          className={cn(
                                            "border-b border-border/30 transition-colors",
                                            !calculated
                                              ? "hover:bg-muted/30"
                                              : isSelected
                                                ? "bg-success/5 border-l-2 border-l-success cursor-pointer"
                                                : canAdd
                                                  ? "hover:bg-muted/30 cursor-pointer"
                                                  : "opacity-40"
                                          )}
                                          onClick={() => {
                                            if (!calculated) return;
                                            if (isSelected || canAdd) {
                                              toggleInvoice(inv.idx, inv);
                                            }
                                          }}
                                        >
                                          {calculated && (
                                            <td className="py-1.5 px-2" style={{ width: "36px" }}>
                                              <Checkbox
                                                checked={isSelected}
                                                disabled={!isSelected && !canAdd}
                                                onCheckedChange={() => toggleInvoice(inv.idx, inv)}
                                                className="h-3.5 w-3.5"
                                                onClick={(e) => e.stopPropagation()}
                                              />
                                            </td>
                                          )}
                                          <td
                                            className="py-1.5 pl-6 pr-2"
                                            style={{ width: calculated ? "26%" : "30%" }}
                                          >
                                            {/* Indented — vendor name suppressed in nested rows */}
                                            <span className="text-[10px] text-muted-foreground">
                                              &nbsp;
                                            </span>
                                          </td>
                                          <td className="py-1.5 px-2" style={{ width: "15%" }}>
                                            <div className="flex items-center gap-1">
                                              <p
                                                className="text-[10px] text-muted-foreground font-mono truncate"
                                                title={inv.invoice_number || ""}
                                              >
                                                {inv.invoice_number || "—"}
                                              </p>
                                              {inv.rowCount > 1 && (
                                                <span className="text-[8px] bg-muted text-muted-foreground rounded px-1 shrink-0">
                                                  x{inv.rowCount}
                                                </span>
                                              )}
                                            </div>
                                          </td>
                                          <td
                                            className="py-1.5 px-2 text-center"
                                            style={{ width: "14%" }}
                                          >
                                            <span className="text-[11px] font-semibold">
                                              {formatCurrency(inv.outstanding)}
                                            </span>
                                          </td>
                                          <td className="py-1.5 px-2" style={{ width: "15%" }}>
                                            <span className="text-[10px] text-muted-foreground">
                                              {inv.early_pay_due_date
                                                ? new Date(
                                                    inv.early_pay_due_date
                                                  ).toLocaleDateString()
                                                : "—"}
                                            </span>
                                          </td>
                                          <td
                                            className="py-1.5 px-2 text-center"
                                            style={{ width: "12%" }}
                                          >
                                            <span className="text-[10px] text-muted-foreground">
                                              {inv.discount_pct != null
                                                ? `${inv.discount_pct.toFixed(1)}%`
                                                : "—"}
                                            </span>
                                          </td>
                                          <td
                                            className="py-1.5 px-2 text-right"
                                            style={{ width: "14%" }}
                                          >
                                            <span className="text-[11px] font-bold text-success">
                                              {formatCurrencyWithCents(inv.potential_savings)}
                                            </span>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Swap impact card and full coverage note — only after Optimize is clicked */}
        {calculated && (
          <div className="space-y-3">
            {/* Swap impact card */}
            {manualOverrides && (
              <Card className="p-4 border-warning/30 bg-warning/5">
                <h4 className="text-xs font-semibold mb-1">Manual Selection Impact</h4>
                <p className="text-[10px] text-muted-foreground mb-2">
                  Your selection differs from the optimal recommendation
                </p>
                {(() => {
                  const optimalSel = greedySelectVendorFirst(ranked, budget);
                  const optimalSavings = ranked
                    .filter((r) => optimalSel.has(r.idx))
                    .reduce((s, r) => s + r.potential_savings, 0);
                  const diff = selectionStats.savings - optimalSavings;
                  return (
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={diff >= 0 ? "default" : "destructive"}
                        className="text-[10px]"
                      >
                        {diff >= 0 ? "+" : ""}
                        {formatCurrencyWithCents(diff)}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">
                        {diff >= 0 ? "vs optimal" : "less than optimal savings"}
                      </span>
                    </div>
                  );
                })()}
              </Card>
            )}

            {/* Full coverage note */}
            {budget >= totalEligibleCost && (
              <Card className="p-4 border-success/30 bg-success/5">
                <p className="text-xs text-success font-medium">
                  ✓ Budget covers all eligible invoices
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  All {filteredRanked.length} early pay opportunities are captured
                </p>
              </Card>
            )}
          </div>
        )}
      </div>
    </>
  );
}
